import { PointGeometry, SecurityInventoryCategory } from '@x-guard/xgac-types/xgac';
import L from 'leaflet';
import MapIcon from '@mui/icons-material/Map';
import GoogleIcon from '@mui/icons-material/Google';
import {
  DialogContent, InputAdornment, Typography,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import moment from 'moment';
import SearchIcon from '@mui/icons-material/Search';
import {
  useEffect, useState, useCallback,
} from 'react';
import { useNotify, useRefresh, useTranslate } from 'react-admin';
import { CustomConfirmDialog } from '../../../../../components/CustomConfirmDialog';
import { GoogleCoordinatesInput } from '../../../components/inputs/GoogleCoordinatesInput';
import { StepDialogCard } from '../../../cards/stepDialogCard';
import { reverseCoordinates } from '../../../../../utils/reverseGeoCode';
import { xgacDataProvider } from '../../../../../dataProviders/xgacDataProvider';
import { securityItemsResource } from '../util';
import { IntegerInput } from '../../../components/inputs/integerInput';
import { getCurrentCustomer } from '../../../../../lib/currentCustomer';
import { notifyWithError } from '../../../../../utils/errorHandling';

export const AddItemDialog = (props: {
  open: boolean;
  category: SecurityInventoryCategory | null;
  mapPlacementActive: boolean;
  onMapPlacementStart: () => void;
  onMapPlacementStop: () => void;
  map: L.Map | null;
  onClose: () => void;
  onBeforeCreate: () => void;
  onAfterCreate: () => void;
}) => {

  const {
    onAfterCreate, onBeforeCreate, onClose, onMapPlacementStart, onMapPlacementStop,
  } = props;

  const translate = useTranslate();
  const refresh = useRefresh();
  const notify = useNotify();

  const [locationInputDialogOpen, setLocationInputDialogOpen] = useState(false);
  const [chosenLocation, setChosenLocation] = useState<PointGeometry | null>(null);
  const [loading, setLoading] = useState(false);
  const [numberOfItems, setNumberOfItems] = useState(1);

  const handleClose = useCallback(() => {

    onMapPlacementStop();
    setLocationInputDialogOpen(false);
    setChosenLocation(null);
    setNumberOfItems(1);
    onClose();

  }, [onClose, onMapPlacementStop]);

  const createItem = useCallback((location: PointGeometry) => {

    if (!props.category) return;

    const { expiryInterval } = props.category;
    // Expiry date is calculated based on the expiry interval and the current date
    const expiryDate = expiryInterval?.type !== undefined && expiryInterval?.value !== undefined
      ? moment().add(expiryInterval.value, expiryInterval.type).toISOString()
      : null;

    setLoading(true);
    const itemDtos = [];
    for (let i = 0; i < numberOfItems; i++) {

      itemDtos.push({
        name: props.category?.name,
        category: {
          _id: props.category?._id,
          _ref: 'SecurityInventoryCategory',
        },
        location: {
          ...location,
          properties: {
            ...location.properties,
            provider: 'static',
          },
        },
        expiryDate,
      });

    }
    onBeforeCreate();

    xgacDataProvider.create(`${securityItemsResource}/add-items`, {
      data: {
        customer: {
          _id: getCurrentCustomer()?.value,
          _ref: 'Customer',
        },
        items: itemDtos,
      },
    }).then(() => {

      refresh();
      onAfterCreate();
      setLoading(false);
      handleClose();

    }).catch((error) => {

      notifyWithError(notify, error);
      setLoading(false);
      handleClose();

    });

  }, [props.category, handleClose, notify, numberOfItems, onAfterCreate, onBeforeCreate, refresh]);

  useEffect(() => {

    if (!props.map || !props.mapPlacementActive) {

      return () => {};

    }

    L.DomUtil.addClass(props.map.getContainer(), 'crosshair-cursor-enabled');

    const handleMapClick = async (event: L.LeafletMouseEvent) => {

      setLoading(true);
      onMapPlacementStop();
      const coordinates = event.latlng;
      const { place, parsedGoogleInput } = await reverseCoordinates(coordinates);

      if (place && parsedGoogleInput) {

        const newLocation = {
          type: 'Point',
          coordinates: [coordinates.lng, coordinates.lat],
          properties: {
            address: {
              formattedAddress: place.formatted_address,
              floor: 0,
              streetName: parsedGoogleInput.route?.long_name,
              streetNumber: parsedGoogleInput.street_number?.long_name || '0',
              city: parsedGoogleInput.locality?.long_name,
              state: parsedGoogleInput.administrative_area_level_1?.long_name,
              countryCode: parsedGoogleInput.country?.short_name || 'NL',
            },
          },
        } as PointGeometry;
        createItem(newLocation);

      } else {

        setLoading(false);

      }

    };

    props.map.on('click', handleMapClick);
    return () => {

      if (props.map) {

        props.map.off('click', handleMapClick);
        L.DomUtil.removeClass(props.map.getContainer(), 'crosshair-cursor-enabled');

      }

    };

  }, [props.map, props.mapPlacementActive, onMapPlacementStop, createItem]);

  const chooserOpen = props.open && props.category !== null && !props.mapPlacementActive && !locationInputDialogOpen && !loading;

  return (
    <>
      <CustomConfirmDialog
        open={chooserOpen}
        handleClose={handleClose}
        dialogTitle={translate('resources.security-inventory-items.text.add_item_dialog', {
          categoryName: props.category?.name,
        })}
        icon1={{
          title: 'resources.zones.text.create_dialog_where_map',
          subTitle: '',
          action: () => {

            if (props.map) {

              onMapPlacementStart();
              return;

            }
            setLocationInputDialogOpen(true);

          },
          icon: <MapIcon />,
        }}
        icon2={{
          title: 'resources.zones.text.create_dialog_where_input',
          subTitle: '',
          action: () => {

            setLocationInputDialogOpen(true);

          },
          icon: <GoogleIcon />,
        }}
      />
      <StepDialogCard
        title="resources.zones.text.create_dialog_where_input"
        open={locationInputDialogOpen}
        onClose={handleClose}
        onNext={() => {

          if (chosenLocation) {

            createItem(chosenLocation);

          }

        }}
        nextLabel="resources.security-inventory-items.text.place_item"
        nextDisabled={!chosenLocation}
        content={
          <DialogContent>
            <Typography
              variant="body1"
              sx={{
                fontSize: '14px',
                marginBottom: '5px',
                fontWeight: 'bold',
              }}
            >
              {translate('general.fields.position.address.name')}
            </Typography>
            <GoogleCoordinatesInput
              className="bhvk-gray-input"
              value={chosenLocation}
              setValue={setChosenLocation}
              label={translate('resources.buttons.text.building_dialog.0.input_label')}
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  width: '100%',
                  height: '40px',
                },
                width: '60%',
              }}
              inputProps={{
                endAdornment: <InputAdornment position={'end'}><SearchIcon/></InputAdornment>,
              }}
              country={['nl', 'be', 'de']}
            />
            <div style={{
              marginTop: '10px',
            }}>
              <span>{translate('resources.security-inventory-items.text.how_many_items')}</span>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                marginTop: '10px',
                marginBottom: '10px',
              }}>
                <IntegerInput value={numberOfItems} setValue={setNumberOfItems} min={1}/>
                <span
                  style={{
                    marginLeft: '10px',
                    fontSize: '15px',
                  }}>
                  {translate('general.text.items')}
                </span>
              </div>
            </div>
          </DialogContent>
        }
      />
      <StepDialogCard open={loading} title={''} content={
        <DialogContent sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
        }}>
          <CircularProgress/>
        </DialogContent>
      } onClose={() => {}}/>
    </>
  );

};
